import React, { useEffect, useState } from "react";
import { Plus, Download, Trash2, Eye, EyeOff, FolderOpen, FileText, Edit3, X as XIcon, UploadCloud, GripVertical } from "lucide-react";
import { PageHead, Loader, EmptyState } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { Badge } from "../components/Badges";
import { fmtDate } from "../lib/format";
import api, { apiErr, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "bee", label: "BEE Certificate", cls: "b-gold" },
  { value: "insurance", label: "Insurance Letter", cls: "b-blue" },
  { value: "compliance", label: "Compliance", cls: "b-violet" },
  { value: "policy", label: "Policy", cls: "b-slate" },
  { value: "other", label: "Other", cls: "b-slate" },
];
const catMeta = (v) => CATEGORIES.find((c) => c.value === v) || CATEGORIES[CATEGORIES.length - 1];

function humanSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function titleFromFilename(name) {
  return (name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
}

export default function Documents() {
  const { can } = useAuth();
  const canEdit = can("documents", "edit");
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busyBulk, setBusyBulk] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/documents");
      setRows(r.data);
    } catch (e) { toast.error(apiErr(e)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  // Drag-and-drop state
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
    try { e.dataTransfer.dropEffect = "move"; } catch (_) {}
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  const persistOrder = async (next) => {
    setRows(next); // optimistic
    try {
      await api.post("/documents/reorder", { ordered_ids: next.map((d) => d.id) });
    } catch (e) {
      toast.error(apiErr(e));
      load(); // revert
    }
  };

  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    const src = dragId;
    setDragId(null); setOverId(null);
    if (!src || !targetId || src === targetId || !rows) return;
    const srcIdx = rows.findIndex((r) => r.id === src);
    const tgtIdx = rows.findIndex((r) => r.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const next = [...rows];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    persistOrder(next);
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.file_name || doc.title || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      setSelected((s) => { const n = new Set(s); n.delete(doc.id); return n; });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleVisible = async (doc) => {
    try {
      await api.patch(`/documents/${doc.id}`, { visible_to_customers: !doc.visible_to_customers });
      toast.success(doc.visible_to_customers ? "Hidden from customer portal" : "Visible to all customers");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const allSelected = rows && rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => {
    if (!rows) return;
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((d) => d.id)));
  };
  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} document${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusyBulk(true);
    try {
      const r = await api.post("/documents/bulk-delete", { ids });
      toast.success(`${r.data.deleted} document${r.data.deleted === 1 ? "" : "s"} deleted`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyBulk(false); }
  };

  return (
    <>
      <PageHead crumb="Records" title="Documents Library"
        sub="Upload PTE documents (BEE certificate, insurance letters, T&Cs). Toggle 'Customer-visible' to publish them across the customer portal — every customer sees the same set. Drag the grip handle to reorder; this order is what customers see."
        actions={canEdit && <button className="btn gold" onClick={() => setAdding(true)} data-testid="add-doc-btn"><Plus size={15} /> Upload documents</button>} />

      {canEdit && selected.size > 0 && (
        <div className="card pad" data-testid="bulk-toolbar"
          style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12,
                   background: "#fff4d6", border: "1px solid #e3d28a" }}>
          <span className="t-strong" style={{ fontSize: 13.5 }}>
            {selected.size} document{selected.size === 1 ? "" : "s"} selected
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</button>
          <button className="btn sm" onClick={bulkDelete} disabled={busyBulk}
            data-testid="bulk-delete"
            style={{ background: "#a32424", color: "#fff", borderColor: "#a32424" }}>
            <Trash2 size={13} /> {busyBulk ? "Deleting…" : `Delete ${selected.size} selected`}
          </button>
        </div>
      )}

      {rows === null && <Loader />}
      {rows && rows.length === 0 && (
        <EmptyState icon={FolderOpen}>
          No documents yet. {canEdit ? "Click 'Upload documents' to add the first batch." : "Ask an admin to upload."}
        </EmptyState>
      )}

      {rows && rows.length > 0 && (
        <div className="card pad" style={{ overflow: "hidden" }}>
          <div className="tbl-wrap">
            <table data-testid="documents-table">
              <thead>
                <tr>
                  {canEdit && <th style={{ width: 24 }} aria-label="Drag to reorder"></th>}
                  {canEdit && (
                    <th style={{ width: 32 }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bulk-select-all" aria-label="Select all" />
                    </th>
                  )}
                  <th>Title</th>
                  <th>Category</th>
                  <th>File</th>
                  <th>Size</th>
                  <th>Valid until</th>
                  <th>Customer-visible</th>
                  <th>Uploaded</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const cm = catMeta(d.category);
                  const isSel = selected.has(d.id);
                  const isOver = overId === d.id && dragId && dragId !== d.id;
                  const isDragging = dragId === d.id;
                  return (
                    <tr key={d.id} data-testid={`doc-row-${d.id}`}
                      onDragOver={canEdit ? onDragOver(d.id) : undefined}
                      onDrop={canEdit ? onDrop(d.id) : undefined}
                      onDragEnd={onDragEnd}
                      style={{
                        ...(isSel ? { background: "#fff8e6" } : null),
                        ...(isDragging ? { opacity: 0.4 } : null),
                        ...(isOver ? { boxShadow: "inset 0 2px 0 var(--gold-champ)" } : null),
                      }}>
                      {canEdit && (
                        <td style={{ padding: "4px 0", cursor: "grab", color: "var(--text-3)" }}
                          draggable
                          onDragStart={onDragStart(d.id)}
                          title="Drag to reorder"
                          data-testid={`doc-drag-${d.id}`}>
                          <GripVertical size={14} />
                        </td>
                      )}
                      {canEdit && (
                        <td>
                          <input type="checkbox" checked={isSel} onChange={() => toggleRow(d.id)} data-testid={`doc-select-${d.id}`} aria-label={`Select ${d.title}`} />
                        </td>
                      )}
                      <td>
                        <span className="cell-2">
                          <span className="t-strong" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FileText size={14} /> {d.title}</span>
                          {d.description && <span className="s t-mut">{d.description}</span>}
                        </span>
                      </td>
                      <td><Badge label={cm.label} cls={cm.cls} /></td>
                      <td className="mono" style={{ fontSize: 12 }}>{d.file_name}</td>
                      <td className="mono">{humanSize(d.size)}</td>
                      <td className="mono">{d.valid_until || "—"}</td>
                      <td>
                        {canEdit ? (
                          <button className="btn sm ghost" onClick={() => toggleVisible(d)}
                            data-testid={`doc-toggle-${d.id}`}
                            style={{ cursor: "pointer" }}
                            title={d.visible_to_customers ? "Click to hide from customer portal" : "Click to publish to customer portal"}>
                            {d.visible_to_customers ? <><Eye size={13} style={{ color: "var(--green)" }} /> Visible</> : <><EyeOff size={13} style={{ color: "var(--text-3)" }} /> Hidden</>}
                          </button>
                        ) : (
                          d.visible_to_customers ? <Badge label="Visible" cls="b-green" /> : <Badge label="Hidden" cls="b-slate" />
                        )}
                      </td>
                      <td>
                        <span className="cell-2">
                          <span className="t-strong">{d.uploaded_by_name || "—"}</span>
                          <span className="s mono t-mut">{fmtDate(d.created_at)}</span>
                        </span>
                      </td>
                      {canEdit && (
                        <td style={{ textAlign: "right" }}>
                          <span style={{ display: "inline-flex", gap: 4 }}>
                            <button className="icon-btn" title="Download" onClick={() => download(d)} data-testid={`doc-dl-${d.id}`}><Download size={14} /></button>
                            <button className="icon-btn" title="Edit metadata" onClick={() => setEditing(d)} data-testid={`doc-edit-${d.id}`}><Edit3 size={14} /></button>
                            <button className="icon-btn" title="Delete" style={{ color: "var(--red)" }} onClick={() => remove(d)} data-testid={`doc-del-${d.id}`}><Trash2 size={14} /></button>
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adding && (
        <BulkUploadModal onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />
      )}
      {editing && (
        <EditModal doc={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      )}
    </>
  );
}

function BulkUploadModal({ onClose, onDone }) {
  // Each entry: { id, file, title, category, visible_to_customers, valid_until, status, error }
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Quick-fill bar — apply to all
  const [qfCategory, setQfCategory] = useState("");
  const [qfValid, setQfValid] = useState("");
  const [qfVisible, setQfVisible] = useState(null); // null|true|false

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.size <= 25 * 1024 * 1024);
    const oversized = Array.from(fileList || []).length - files.length;
    if (oversized > 0) toast.error(`${oversized} file${oversized === 1 ? " was" : "s were"} skipped (over 25 MB limit)`);
    setItems((prev) => {
      const next = [...prev];
      for (const f of files) {
        next.push({
          id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
          file: f,
          title: titleFromFilename(f.name),
          category: "other",
          visible_to_customers: true,
          valid_until: "",
          status: "pending",
          error: null,
        });
      }
      return next;
    });
  };

  const setRow = (id, patch) => setItems((p) => p.map((it) => it.id === id ? { ...it, ...patch } : it));
  const removeRow = (id) => setItems((p) => p.filter((it) => it.id !== id));

  const applyQuickFill = () => {
    setItems((p) => p.map((it) => ({
      ...it,
      category: qfCategory || it.category,
      valid_until: qfValid || it.valid_until,
      visible_to_customers: qfVisible !== null ? qfVisible : it.visible_to_customers,
    })));
    toast.success("Applied to all rows");
  };

  const submitAll = async () => {
    if (!items.length) return toast.error("Pick at least one file to upload");
    const bad = items.filter((it) => !it.title.trim());
    if (bad.length) return toast.error(`${bad.length} row${bad.length === 1 ? " is" : "s are"} missing a title`);
    setBusy(true);
    setProgress({ done: 0, total: items.length });
    const token = localStorage.getItem("pte_token");
    let succeeded = 0;
    const queue = [...items];
    // Limit to 3 parallel uploads to play nicely with the proxy
    const workers = Array.from({ length: Math.min(3, queue.length) }, () => (async () => {
      while (queue.length) {
        const it = queue.shift();
        setRow(it.id, { status: "uploading", error: null });
        try {
          const fd = new FormData();
          fd.append("title", it.title.trim());
          fd.append("category", it.category);
          fd.append("description", "");
          fd.append("visible_to_customers", it.visible_to_customers ? "true" : "false");
          if (it.valid_until) fd.append("valid_until", it.valid_until);
          fd.append("file", it.file);
          const resp = await fetch(`${API}/documents`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
          if (!resp.ok) {
            const j = await resp.json().catch(() => ({}));
            throw new Error(j.detail || resp.statusText);
          }
          setRow(it.id, { status: "done", error: null });
          succeeded += 1;
        } catch (e) {
          setRow(it.id, { status: "failed", error: e.message || "Upload failed" });
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    })());
    await Promise.all(workers);
    setBusy(false);
    if (succeeded > 0) {
      toast.success(`${succeeded} document${succeeded === 1 ? "" : "s"} uploaded`);
    }
    const failed = items.length - succeeded;
    if (failed === 0) {
      onDone();
    } else {
      toast.error(`${failed} upload${failed === 1 ? "" : "s"} failed — see rows below`);
      // Keep modal open so user can retry/remove failed rows
      setItems((p) => p.filter((it) => it.status !== "done"));
    }
  };

  return (
    <Modal open onClose={busy ? () => {} : onClose} wide
      icon={UploadCloud}
      title="Upload documents"
      subtitle="Select multiple files at once. You can edit title and metadata per row before uploading."
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>{busy ? "Uploading…" : "Cancel"}</button>
        <button className="btn gold" onClick={submitAll} disabled={busy || !items.length} data-testid="bulk-upload-submit">
          {busy
            ? `Uploading ${progress.done}/${progress.total}…`
            : `Upload ${items.length || ""} document${items.length === 1 ? "" : "s"}`}
        </button>
      </>}>
      {/* Picker */}
      <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 14 }}>
        <label className="t-strong" style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Choose files to upload</label>
        <input type="file" multiple className="input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="doc-file-input" />
        <div className="s t-mut" style={{ marginTop: 4 }}>Hold ⌘/Ctrl or Shift to pick multiple. Max 25 MB each. You can add more files at any time below.</div>
      </div>

      {/* Quick-fill */}
      {items.length > 1 && (
        <div className="card pad" style={{ marginBottom: 14, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "0 0 auto" }} className="t-strong">Apply to all:</div>
          <div className="fld" style={{ minWidth: 160 }}>
            <label>Category</label>
            <select className="input" value={qfCategory} onChange={(e) => setQfCategory(e.target.value)} data-testid="qf-category">
              <option value="">— keep current —</option>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="fld" style={{ minWidth: 150 }}>
            <label>Valid until</label>
            <input type="date" className="input" value={qfValid} onChange={(e) => setQfValid(e.target.value)} />
          </div>
          <div className="fld" style={{ minWidth: 160 }}>
            <label>Visibility</label>
            <select className="input" value={qfVisible === null ? "" : qfVisible ? "true" : "false"}
              onChange={(e) => setQfVisible(e.target.value === "" ? null : e.target.value === "true")} data-testid="qf-visibility">
              <option value="">— keep current —</option>
              <option value="true">Visible to customers</option>
              <option value="false">Hidden from customers</option>
            </select>
          </div>
          <button className="btn ghost" onClick={applyQuickFill} data-testid="qf-apply">Apply</button>
        </div>
      )}

      {/* Rows */}
      {items.length === 0 ? (
        <div className="empty" style={{ padding: 22 }}>
          <UploadCloud size={28} style={{ margin: "0 auto 10px", display: "block", opacity: 0.5 }} />
          Pick one or more files above. You can edit each row's title, category and visibility before uploading.
        </div>
      ) : (
        <div className="tbl-wrap">
          <table data-testid="bulk-upload-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Title</th>
                <th>Category</th>
                <th>Visibility</th>
                <th>Valid until</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`bulk-row-${it.id}`}>
                  <td>
                    <span className="cell-2">
                      <span className="t-strong mono" style={{ fontSize: 12 }}>{it.file.name}</span>
                      <span className="s t-mut">{humanSize(it.file.size)}</span>
                    </span>
                  </td>
                  <td>
                    <input className="input" style={{ padding: "4px 8px" }}
                      value={it.title}
                      onChange={(e) => setRow(it.id, { title: e.target.value })}
                      disabled={busy}
                      data-testid={`bulk-title-${it.id}`} />
                  </td>
                  <td>
                    <select className="input" style={{ padding: "4px 8px" }}
                      value={it.category}
                      onChange={(e) => setRow(it.id, { category: e.target.value })}
                      disabled={busy}>
                      {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <input type="checkbox" checked={it.visible_to_customers}
                        onChange={(e) => setRow(it.id, { visible_to_customers: e.target.checked })}
                        disabled={busy} />
                      Visible
                    </label>
                  </td>
                  <td>
                    <input type="date" className="input" style={{ padding: "4px 8px" }}
                      value={it.valid_until}
                      onChange={(e) => setRow(it.id, { valid_until: e.target.value })}
                      disabled={busy} />
                  </td>
                  <td>
                    {it.status === "pending" && <Badge label="Ready" cls="b-slate" />}
                    {it.status === "uploading" && <Badge label="Uploading…" cls="b-blue" />}
                    {it.status === "done" && <Badge label="Uploaded" cls="b-green" />}
                    {it.status === "failed" && <span title={it.error}><Badge label="Failed" cls="b-red" /></span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button className="icon-btn" title="Remove from batch"
                      onClick={() => removeRow(it.id)} disabled={busy}
                      data-testid={`bulk-remove-${it.id}`}>
                      <XIcon size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function EditModal({ doc, onClose, onDone }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    category: doc.category || "other",
    description: doc.description || "",
    valid_until: doc.valid_until || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/documents/${doc.id}`, form);
      toast.success("Updated");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Edit document" subtitle={doc.file_name}
      footer={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn gold" onClick={save} disabled={busy} data-testid="doc-save-edit">Save</button></>}>
      <div className="form-grid">
        <div className="fld" style={{ gridColumn: "1 / -1" }}>
          <label>Title</label>
          <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="fld">
          <label>Category</label>
          <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Valid until</label>
          <input type="date" className="input" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
        </div>
        <div className="fld" style={{ gridColumn: "1 / -1" }}>
          <label>Description</label>
          <textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}
